/**
 * The farm page's Setup tab: everything installed on this farm, each one tap
 * from its own page. It replaces the Devices page as the way in from a farm,
 * which was a list to click through before reaching the node somebody wanted.
 *
 *   site   → the controller page (Nodes, Schedules, Live map, Events, Settings)
 *   node   → the controller page with that node's card ringed (?node=<mac>)
 *   sensor → the sensor page, where soil levels are set
 *
 * Read from the overview the farm page already holds; nothing extra is fetched.
 */
import { Link } from 'react-router-dom';
import {
  Activity,
  Calendar,
  ChevronRight,
  Map as MapIcon,
  PauseCircle,
  Plus,
  Radio,
  Settings,
  Terminal,
} from 'lucide-react';
import type { OverviewController, OverviewFarm } from '../../services/irrigationOverview';
import { nodeState, nodeStateDot, nodeStateLabel } from '../../utils/nodeState';
import { formatDateTimeInTz } from '../../utils/timezone';
import FarmAlertRecipients from './FarmAlertRecipients';

function transportLabel(t: string | null): string {
  if (t === 'xbee') return 'XBee bridge';
  if (t === 'espnow') return 'ESP-NOW';
  if (t === 'lora') return 'LoRa';
  if (t === 'http') return 'Wi-Fi';
  return 'Controller';
}

function SiteCard({
  farmId,
  controller,
  timezone,
  isAdmin,
}: {
  farmId: number;
  controller: OverviewController;
  timezone: string;
  isAdmin: boolean;
}) {
  const base = `/irrigation/espnow/${encodeURIComponent(controller.controller_id)}`;
  const links = [
    { to: base, label: 'Nodes', icon: <Radio className="h-4 w-4" /> },
    { to: `/irrigation/farms/${farmId}?tab=schedules&site=${encodeURIComponent(controller.controller_id)}`, label: 'Schedules', icon: <Calendar className="h-4 w-4" /> },
    { to: `${base}?tab=map`, label: 'Live map', icon: <MapIcon className="h-4 w-4" /> },
    { to: `${base}?tab=events`, label: 'Events', icon: <Terminal className="h-4 w-4" /> },
    ...(isAdmin ? [{ to: `${base}?tab=settings`, label: 'Settings', icon: <Settings className="h-4 w-4" /> }] : []),
  ];
  const online = controller.routers.filter((r) => nodeState(r) === 'online').length;

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-100 p-4">
        <div className="min-w-0">
          <p className="flex items-center gap-2 break-words text-base font-semibold text-gray-900">
            <span
              className={`h-2.5 w-2.5 shrink-0 rounded-full ${controller.online ? 'bg-green-500' : 'bg-red-500'}`}
              title={controller.online ? 'Online' : 'Offline'}
            />
            {controller.controller_name || controller.controller_id}
          </p>
          <p className="mt-0.5 text-xs text-gray-500">
            {transportLabel(controller.transport)} · {controller.online ? 'online' : 'offline'} ·{' '}
            {online}/{controller.routers.length} node{controller.routers.length === 1 ? '' : 's'} answering
          </p>
          {controller.watering_paused_at && (
            <p className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-700">
              <PauseCircle className="h-3.5 w-3.5 shrink-0" /> Watering is stopped here
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-gray-100 px-4 py-3">
        {links.map((l) => (
          <Link
            key={l.label}
            to={l.to}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-blue-300 hover:text-blue-700"
          >
            {l.icon}
            {l.label}
          </Link>
        ))}
      </div>

      {controller.routers.length === 0 ? (
        <p className="p-4 text-sm text-gray-500">No nodes have reported to this site yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {controller.routers.map((r) => {
            const state = nodeState(r);
            return (
              <li key={r.router_mac}>
                <Link
                  to={`${base}?node=${encodeURIComponent(r.router_mac)}`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50"
                >
                  <span className={`h-2 w-2 shrink-0 rounded-full ${nodeStateDot(state)}`} title={nodeStateLabel(state)} />
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-sm font-medium text-gray-900">
                      {r.name || r.router_mac}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {nodeStateLabel(state)}
                      {r.rssi_dbm != null && ` · ${r.rssi_dbm} dBm`}
                      {r.last_seen && ` · heard ${formatDateTimeInTz(r.last_seen, timezone)}`}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function FarmSetupPanel({
  farm,
  timezone,
  isAdmin,
}: {
  farm: OverviewFarm;
  timezone: string;
  isAdmin: boolean;
}) {
  return (
    <div className="space-y-8">
      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
            <Radio className="h-4 w-4 text-blue-500" />
            Sites and nodes
          </h2>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/irrigation/network?farm=${farm.id}`}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-blue-300 hover:text-blue-700"
            >
              <MapIcon className="h-4 w-4" /> Network map
            </Link>
            {isAdmin && (
              <Link
                to="/irrigation/espnow/admin"
                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" /> Add or pair a device
              </Link>
            )}
          </div>
        </div>
        {farm.controllers.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center text-sm text-gray-500">
            No controller is on this farm yet.
            {isAdmin ? ' Add or pair one, then assign it to this farm.' : ' An Irrigation Admin can add one.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {farm.controllers.map((c) => (
              <SiteCard key={c.controller_id} farmId={farm.id} controller={c} timezone={timezone} isAdmin={isAdmin} />
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
          <Activity className="h-4 w-4 text-violet-500" />
          Sensors ({farm.sensors.length})
        </h2>
        {farm.sensors.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center text-sm text-gray-500">
            No sensors are assigned to this farm.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
            {farm.sensors.map((s) => {
              const soil = s.soil_channels ?? [];
              const noLevels = soil.filter((c) => !c.levels).length;
              return (
                <li key={s.device_id}>
                  <Link
                    to={`/irrigation/sensors/${encodeURIComponent(s.device_id)}`}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50"
                  >
                    <span className={`h-2 w-2 shrink-0 rounded-full ${s.online ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block break-words text-sm font-medium text-gray-900">
                        {s.name || s.device_id}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {s.type_display_name || s.type_key}
                        {soil.length > 0 && ` · ${soil.length} soil channel${soil.length === 1 ? '' : 's'}`}
                      </span>
                      {noLevels > 0 && (
                        <span className="block text-xs font-medium text-amber-700">
                          {noLevels} without moisture levels, so never flagged dry
                        </span>
                      )}
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section>
        <FarmAlertRecipients farmId={farm.id} canEdit={isAdmin} />
      </section>

      <p className="text-xs text-gray-500">
        Looking for something not on this farm?{' '}
        <Link to="/irrigation/espnow" className="font-semibold text-blue-600 hover:text-blue-800">
          All devices
        </Link>
      </p>
    </div>
  );
}
